<template>
  <section
    v-editable="blok"
    class="wp-hero section--gray"
    :data-section-uid="blok._uid"
  >
    <div class="container container--section">
      <div class="wp-hero__wrapper">
        <div class="wp-hero__description">
          <p class="wp-hero__subtitle">
            {{ blok.small_title }}
          </p>
          <h1 class="wp-hero__title">
            {{ blok.title }}
          </h1>
          <p class="wp-hero__text">
            {{ blok.subtitle }}
          </p>
          <BaseButton
            :title="blok.cta_text"
            @click="scrollToForm('#form-section', 40)"
          >
            <template #text>
              {{ blok.cta_text }}
            </template>
          </BaseButton>
          <img
            width="190"
            height="44"
            src="@/assets/images/dots.png"
            class="fluid-image wp-hero__accent-image"
          />
        </div>
        <div class="wp-hero__image-wrapper">
          <BaseNuxtImage
            width="500"
            height="auto"
            class="fluid-image wp-hero__image"
            provider="storyblok"
            :src="blok.illustration.filename"
            :alt="blok.illustration.alt"
          />
          <div class="wp-hero__socials">
            <a
              class="social-media-icon social-media-icon--copy"
              @click="shareToClipboard()"
            >
              <span class="icon icon--share-link" />
              <p
                class="social-media-icon__tooltip"
                :class="{
                  'social-media-icon__tooltip--visible': showClipboardTooltip,
                }"
                v-text="$t(tooltipText)"
              />
            </a>
            <a class="social-media-icon" @click.prevent="shareToLinkedin()">
              <span class="icon icon--linkedin" />
            </a>
            <a class="social-media-icon" @click.prevent="shareToTwitter()">
              <span class="icon icon--twitter" />
            </a>
            <a class="social-media-icon" @click.prevent="shareToFacebook()">
              <span class="icon icon--facebook" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { SbHeroWhitePapersStoryblok } from '@/types'
import { scrollTo } from '@/utils/helpers'
// import Image1 from '@/assets/icons/logo.svg'
interface Props {
  blok: SbHeroWhitePapersStoryblok
  shareTitle?: string
}
const props = withDefaults(defineProps<Props>(), {
  shareTitle: '',
})
const route = useRoute()

const shareUrl = `https://infermedica.com${route.fullPath}`
const showClipboardTooltip = ref(false)
const tooltipText = ref('')

const scrollToForm = scrollTo

const shareToClipboard = () => {
  copyToClipboard(shareUrl)
    .then(() => {
      if (showClipboardTooltip.value) {
        return true
      }
      tooltipText.value = 'common.copied_to_clipboard'
      toggleTootlipOn()
    })
    .catch((err) => {
      if (showClipboardTooltip.value) {
        return true
      }
      tooltipText.value = 'common.unable_to_copy'
      toggleTootlipOn()
      throw new Error(err)
    })
}

const toggleTootlipOn = () => {
  showClipboardTooltip.value = true
  setTimeout(() => {
    showClipboardTooltip.value = false
  }, 1500)
}

const copyToClipboard = (textToCopy: string) => {
  // navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
    // navigator clipboard api method'
    return navigator.clipboard.writeText(textToCopy)
  }
  // text area method
  const textArea = document.createElement('textarea')
  textArea.value = textToCopy
  textArea.style.position = 'fixed'
  textArea.style.left = '-999999px'
  textArea.style.top = '-999999px'
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()
  return new Promise((resolve, reject) => {
    document.execCommand('copy')
      ? resolve()
      : reject(new Error('Something went wrong'))
    textArea.remove()
  })
}

const shareToTwitter = () => {
  const link = `https://twitter.com/intent/tweet?text=${props.shareTitle}&url=${shareUrl}`
  window.open(encodeURI(link), 'twitter-share', 'width=550,height=235')
}
const shareToLinkedin = () => {
  const link = `https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&summary=${props.shareTitle}`
  window.open(link, 'linkedin-share', 'width=550,height=474')
}
const shareToFacebook = () => {
  const link = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`
  window.open(link, 'facebook-share', 'width=580,height=296')
}
</script>

<style lang="scss" scoped>
.wp-hero {
  padding-top: 60px;
  padding-bottom: 80px;
  overflow-x: hidden;

  @media (min-width: $breakpoint-md) {
    padding-top: 80px;
  }

  @media (min-width: $breakpoint-lg) {
    padding-bottom: 128px;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;

    @media (min-width: $breakpoint-lg) {
      flex-direction: row;
    }
  }

  &__title,
  &__subtitle {
    margin-bottom: 24px;
  }

  &__subtitle {
    @include heading-subheading;
  }

  @media (max-width: $breakpoint-md - 1px) {
    &__title {
      @include heading-2;
    }
  }

  &__text {
    margin-bottom: 32px;

    @media (min-width: $breakpoint-md) {
      line-height: 30px;
    }
  }

  &__description {
    position: relative;
    margin-bottom: 44px;
  }

  &__accent-image {
    display: none;

    @media (min-width: $breakpoint-lg) {
      position: absolute;
      top: 486px;
      right: -26px;
      display: block;
      width: 190px;
    }
  }

  &__image {
    display: block;
    width: 109.5%;
    max-width: 109.5%;
    margin: 0 auto 24px;

    @media (min-width: $breakpoint-md) {
      width: 100%;
      max-width: 100%;
    }

    @media (min-width: $breakpoint-lg) {
      margin: 0;
    }
  }

  &__socials {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;

    @media (min-width: $breakpoint-lg) {
      justify-content: center;
      margin-right: 44px;
    }

    .social-media-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      margin: 0 4px;
      cursor: pointer;
      background-color: var(--white);
      background-repeat: no-repeat;
      background-position: center;
      border: 1px solid var(--gray-400);
      border-radius: 50%;
      transition: border 0.12s ease;

      &:hover {
        border: 1px solid var(--gray-800);
      }

      &--copy {
        position: relative;
      }

      .icon {
        display: block;
        width: 20px;
        height: 20px;

        &--facebook {
          background-image: url('@/assets/icons/social-media/20-facebook.svg');
        }

        &--twitter {
          background-image: url('@/assets/icons/social-media/20-twitter.svg');
          background-position: center;
          background-repeat: no-repeat;
        }

        &--linkedin {
          background-image: url('@/assets/icons/social-media/20-linkedin.svg');
        }

        &--share-link {
          background-image: url('@/assets/icons/social-media/20-link.svg');
        }
      }

      &__tooltip {
        position: absolute;
        top: 48px;
        left: 4px;
        display: block;
        padding: 1px 16px 2px;
        font-size: var(--font-size-paragraph-s);
        color: var(--white);
        white-space: nowrap;
        pointer-events: none;
        background-color: rgb(17 41 80 / 96%);
        border-radius: 16px;
        opacity: 0;
        transition:
          opacity 0.16s,
          transform 0.12s;
        transform: translateX(-8px);

        &--visible {
          z-index: 21;
          pointer-events: auto;
          opacity: 1;
          transform: translateX(0);
        }

        @media (min-width: $breakpoint-md) {
          top: 48px;
          left: 0;
        }

        @media (min-width: $breakpoint-lg) {
          top: 6px;
          left: -192px;
        }
      }
    }
  }

  @media (min-width: $breakpoint-md) and (max-width: $breakpoint-lg - 1px) {
    &__description {
      width: 75%;
    }

    &__image-wrapper {
      display: flex;
      flex-direction: row-reverse;
      align-items: flex-end;
      margin-top: -108px;
    }

    &__image {
      max-width: 58.33%;
      margin: 0 0 0 auto;
    }

    &__socials {
      transform: translateY(-6.7vw);
    }
  }

  @media (min-width: $breakpoint-lg) {
    &__description,
    &__image-wrapper {
      width: 50%;
    }

    &__description {
      padding-top: 32px;
      margin-bottom: 0;
    }

    &__image-wrapper {
      transform: translateX(16.6%);
    }
  }
}

.section--gray {
  background-color: var(--gray-100);
}
</style>
